import { ZodIssue, ZodSchema } from 'zod';
import {
  contactResponseSchema,
  PROSPECT_CONTACT_PATH,
  type ContactSaveRequest,
  type ContactSaveResponse,
} from './prospectSchema';

export { PROSPECT_CONTACT_PATH } from './prospectSchema';

export interface APIRequestOptions extends RequestInit {
  cacheEnabled?: boolean;
  bustCache?: boolean;
}

export interface APIClientConfig {
  baseUrl: string;
  defaultHeaders?: Record<string, string>;
  cache?: Map<string, Response>;
  newRelic?: {
    noticeError?: (error: Error, context: Record<string, unknown>) => void;
  };
  strictValidation?: boolean;
}

export class ProspectApiClient {
  public cache: Map<string, Response>;

  constructor(public readonly config: APIClientConfig) {
    const defaultConfig: APIClientConfig = {
      baseUrl: '',
      defaultHeaders: {
        'Content-Type': 'application/json',
      },
      newRelic: {
        noticeError: (error: Error, context: Record<string, unknown>) => {
          console.error(
            'Error in ProspectApiClient: noticeError',
            error,
            context,
          );
        },
      },
      strictValidation: false,
    };
    this.config = { ...defaultConfig, ...config };
    this.cache = config.cache ?? new Map();
  }

  public getApiName(): string {
    return 'prospect-api-client';
  }

  public async get(
    path: string,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Response> {
    const cacheKey = `${this.config.baseUrl}${path}`;

    if (options.bustCache) {
      this.bustCache(cacheKey);
    }

    if (this.shouldUseCache(cacheKey, options)) {
      return this.cache.get(cacheKey)!.clone();
    }

    const response = await fetch(`${this.config.baseUrl}${path}`, {
      ...options,
      headers: {
        ...this.config.defaultHeaders,
        ...options.headers,
      },
    });

    if (options.cacheEnabled && response.ok) {
      this.cache.set(cacheKey, response.clone());
    }

    return response;
  }

  public async post<Body>(
    path: string,
    body: Body,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Response> {
    const response = await fetch(`${this.config.baseUrl}${path}`, {
      method: 'POST',
      ...options,
      headers: {
        ...this.config.defaultHeaders,
        ...options.headers,
      },
      body: JSON.stringify(body),
    });
    return response;
  }

  private bustCache(cacheKey: string): void {
    this.cache.delete(cacheKey);
  }

  private shouldUseCache(
    cacheKey: string,
    options: APIRequestOptions,
  ): boolean {
    if (!options.cacheEnabled) {
      return false;
    }

    return this.cache.has(cacheKey);
  }

  public hasError(response: Response): boolean {
    return response.status >= 400;
  }

  public async toJson<T>(response: Response, schema: ZodSchema<T>): Promise<T> {
    const data = await response.json();
    const validated = schema.safeParse(data);

    if (!validated.success) {
      const error = new Error(`Invalid response for ${this.getApiName()}`);
      console.error(error);
      console.log(this.buildErrorMessage(validated.error.errors));

      if (this.config.strictValidation) {
        throw error;
      }

      return data as T;
    }

    return validated.data;
  }

  public reportError(error: Error, context?: Record<string, unknown>): void {
    if (this.config.newRelic?.noticeError) {
      this.config.newRelic.noticeError(error, context || {});
    }
  }

  private buildErrorMessage(errors: ZodIssue[]): string[] {
    return errors.map(
      (error) => `Path: ${error.path.join('.')}, Message: ${error.message}`,
    );
  }

  async postContact(body: ContactSaveRequest): Promise<ContactSaveResponse> {
    const response = await this.post(PROSPECT_CONTACT_PATH, body);

    if (this.hasError(response)) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return this.toJson(response, contactResponseSchema);
  }
}
